/**
 * Web PushAdapter — thin wrapper over the browser Notifications API.
 *
 * Legacy Shopista did not use push notifications. This impl is intentionally
 * minimal:
 *   - `requestPermission()` defers to the platform API if available; returns
 *     'denied' on older browsers / non-secure contexts.
 *   - `notify()` shows a one-shot notification when granted, else no-ops.
 *   - Neither method throws. Orchestrators never block on push results.
 *
 * Real push (service worker + FCM/APNs) is out of scope for T6 and the
 * initial web MVP — it is mobile's concern (Phase 4+).
 */

import type { NotifyInput, PushAdapter } from '@shopista/shared/stores';

/**
 * Narrow DOM surface we rely on — avoids leaning on `lib.dom`'s `Notification`
 * directly so a test environment without `Notification` stays compilable.
 */
interface NotificationCtor {
  new (title: string, options?: { body?: string; tag?: string }): unknown;
  permission: 'default' | 'granted' | 'denied';
  requestPermission(): Promise<NotificationPermission>;
}

type NotificationPermission = 'default' | 'granted' | 'denied';

function getNotificationCtor(): NotificationCtor | null {
  if (typeof window === 'undefined') return null;
  const maybe = (window as unknown as { Notification?: unknown }).Notification;
  if (!maybe || typeof maybe !== 'function') return null;
  return maybe as NotificationCtor;
}

export function createWebPushAdapter(): PushAdapter {
  return {
    async requestPermission(): Promise<'granted' | 'denied' | 'default'> {
      const Ctor = getNotificationCtor();
      if (!Ctor) return 'denied';
      if (Ctor.permission === 'granted') return 'granted';
      if (Ctor.permission === 'denied') return 'denied';
      try {
        const result = await Ctor.requestPermission();
        return result;
      } catch {
        return 'denied';
      }
    },

    notify(input: NotifyInput): void {
      const Ctor = getNotificationCtor();
      if (!Ctor) return;
      if (Ctor.permission !== 'granted') return;
      try {
        const options: { body?: string; tag?: string } = {};
        if (input.body !== undefined) options.body = input.body;
        if (input.tag !== undefined) options.tag = input.tag;
        new Ctor(input.title, options);
      } catch {
        // swallow — fire-and-forget by contract
      }
    },
  };
}
